import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PercentChanged from "../percentChanged/percentChanged";
import MoneyDisplay from "../moneyDisplay/moneyDisplay";
import AnimatedNumber from "react-animated-number";
import AnimatedDonut from "react-animated-donut";
import { Tooltip } from "react-tooltip";
import striptags from "striptags";
import { asNumberString } from "../../utils/formatters";
import { isFinite } from "lodash";
import { useLightTheme } from "../../hooks/useLightTheme";
import "./donut.scss";

export function getColor(percent) {
  let p = percent / 100;
  let luma = 58;
  let increase = 1.42 + p - 0.82;

  return "hsl(213, 80%, " + luma / increase + "%)";
}

function DonutChart({
  value = 16,
  size = 116,
  strokewidth = 21,
  customTitleStyle = "",
  attended: attendedCount = 0,
  totalAttended = 0,
  currencySymbol = "",
  centerToolTip = "",
  growth = 0.23,
  showForecast = false,
  hidePercentChanged = false,
  url = "",
  fontSize = 15,
  ...props
}) {
  const donutTitle = striptags(props.type);
  const donutTitleRef = useRef();

  useEffect(() => {
    let el = donutTitleRef.current;
    const hasOverflow = el.scrollHeight > el.clientHeight;

    if (hasOverflow) {
      el.setAttribute("data-tooltip-content", donutTitle);
    }
  }, []);

  let formatForecastData = () => {
    if (props.forecastValue < value) {
      return [
        { value: props.forecastValue, color: "#fd6a3e" },
        { value: value - props.forecastValue, color: strokecolor },
        { value: 100 - value, color: "transparent" }
      ];
    }

    return [
      { value, color: strokecolor },
      { value: props.forecastValue - value, color: "#fd6a3e" },
      { value: 100 - props.forecastValue, color: "transparent" }
    ];
  };

  const isLight = useLightTheme();

  const halfsize = size * 0.5;
  const radius = halfsize - strokewidth * 0.5;
  const strokecolor = isLight ? (value >= 50 ? "#1c64f2" : "#76a9fa") : getColor(value);
  const haveDollarValue = isFinite(props.dollarValue);
  const hasMinusButton = props.hasMinusButton;
  const attended = attendedCount > 0;
  let moneyContainer = null;
  if (haveDollarValue) {
    moneyContainer = (
      <MoneyDisplay
        color={isLight ? "#3f83f8" : null}
        value={props.dollarValue}
        size={fontSize}
        currency={currencySymbol}
        url={url}
        decimal={props.decimal}
      />
    );
  }

  // const indicatorstyle = {strokeWidth: props.strokewidth, strokeDasharray: dashval, stroke: strokecolor}
  const rotateval = "rotate(-90 " + halfsize + "," + halfsize + ")";
  const forecastData = formatForecastData();

  return (
    <div className="donut-wrapper">
      {hasMinusButton && (
        <button
          onClick={props.onMinusButtonClick}
          className="minus-button"
          data-testid="minus-button"
        >
          &ndash;
        </button>
      )}
      <div style={{ minHeight: size, minWidth: size }}>
        <svg
          width={size}
          height={size}
          data-testid="donut-chart"
          className="donut-chart"
        >
          <circle
            r={radius}
            cx={halfsize}
            cy={halfsize}
            transform={rotateval}
            style={{ strokeWidth: strokewidth }}
            className="donutchart-track"
          />
          {/*TODO: fix error in AnimatedDonut*/}
          {showForecast ? (
            <AnimatedDonut data={forecastData} speed={(5 * value) / 100} width={26} />
          ) : (
            <AnimatedDonut
              data={[
                { value, color: strokecolor },
                { value: 100 - value, color: "transparent" }
              ]}
              speed={(5 * value) / 100}
              width={26}
            />
          )}
          <text
            data-tooltip-content={centerToolTip}
            data-tooltip-id="donut-tooltip"
            className="donutchart-text"
            x={halfsize}
            y={halfsize + 7}
          >
            <AnimatedNumber
              component="tspan"
              value={value}
              className="donutchart-text-val"
              duration={1000}
              decimal={props.decimal}
              stepPrecision={0}
              formatValue={(n) => n.toFixed(0)}
            />
            <tspan className="donutchart-text-percent">%</tspan>
          </text>
        </svg>
      </div>
      <div className={"donut-title " + customTitleStyle}>
        <p
          data-tooltip-content=""
          data-tooltip-id="donut-tooltip"
          ref={donutTitleRef}
          className="donutTooltip"
        >
          {donutTitle}
        </p>
      </div>
      {attended && (
        <div className="attended">
          <div>
            <p className="totals" data-testid="attended-totals">
              {asNumberString(attendedCount)} of {asNumberString(totalAttended)}
            </p>
            <p className="attended-sub">attended</p>
          </div>
        </div>
      )}
      <div className="donut-title money-display-wrapper" data-testid="money-display-wrapper">
        {moneyContainer}
      </div>
      {props.average == null ? null : (
        <div className="average">
          <div className="average__text">{props.averageText}</div>
          <div className="average__value">{props.average}</div>
        </div>
      )}
      {hidePercentChanged ? null : (
        <div className="donut-title percent-changed-wrapper" data-testid="percent-changed-wrapper">
          <PercentChanged changeValue={growth} toolTip={props.toolTip} showBorder />
        </div>
      )}
      <Tooltip id="donut-tooltip" float />
    </div>
  );
}

export default DonutChart;

DonutChart.propTypes = {
  value: PropTypes.any,
  size: PropTypes.number,
  strokewidth: PropTypes.number,
  type: PropTypes.string,
  growth: PropTypes.number,
  dollarValue: PropTypes.number,
  fontSize: PropTypes.number,
  onMinusButtonClick: PropTypes.func,
  hasMinusButton: PropTypes.bool,
  customTitleStyle: PropTypes.string,
  attended: PropTypes.number,
  totalAttended: PropTypes.number,
  currencySymbol: PropTypes.string,
  toolTip: PropTypes.string,
  centerToolTip: PropTypes.string,
  forecastValue: PropTypes.number,
  showForecast: PropTypes.bool,
  hidePercentChanged: PropTypes.bool,
  url: PropTypes.string,
  average: PropTypes.number,
  averageText: PropTypes.string
};
